import { useState } from 'react';

import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type TextEntryProps = {
  label: string;
  placeholder: string;
  submitLabel: string;
  busyLabel: string;
  isBusy: boolean;
  error?: string | null;
  testID: string;
  autoFocus?: boolean;
  /** Holds the text above the field, so a caller can act on what is typed but not yet submitted. */
  value?: string;
  onChangeText?: (text: string) => void;
  /** Resolves to whether it was accepted; the text is cleared only then, so a failure keeps it. */
  onSubmit: (text: string) => Promise<boolean>;
};

/**
 * A typed answer the server turns into reply text: "something else" on the
 * card, a sentence of the expert's own while editing. The keyboard's own
 * dictation works here, which is how an expert speaks one for now.
 *
 * Autocorrect is off: it rewrites drug names it does not know, and in QA it
 * turned "olaparib" into "plantain" with nothing on screen to show it had.
 */
export function TextEntry({
  label,
  placeholder,
  submitLabel,
  busyLabel,
  isBusy,
  error,
  testID,
  autoFocus = false,
  value,
  onChangeText,
  onSubmit,
}: TextEntryProps) {
  const [ownText, setOwnText] = useState('');

  const text = value ?? ownText;

  const setText = onChangeText ?? setOwnText;

  const trimmed = text.trim();

  const submit = async () => {
    if (!trimmed || isBusy) {
      return;
    }

    if (await onSubmit(trimmed)) {
      setText('');
    }
  };

  return (
    <VStack className="gap-2">
      <Text size="sm" className="font-medium text-typography-900">
        {label}
      </Text>

      <Textarea className="min-h-20 rounded-lg border-outline-500" isDisabled={isBusy}>
        <TextareaInput
          value={text}
          onChangeText={setText}
          placeholder={placeholder}
          testID={`${testID}-input`}
          autoFocus={autoFocus}
          autoCorrect={false}
          aria-label={label}
          {...getAccessibilityProps({ label })}
        />
      </Textarea>

      {error ? (
        <Text size="sm" className="text-error-700" accessibilityLiveRegion="polite" role="alert">
          {error}
        </Text>
      ) : null}

      <Button
        size="lg"
        variant="outline"
        action="secondary"
        className="min-h-12 self-start"
        isDisabled={!trimmed || isBusy}
        onPress={submit}
        testID={`${testID}-submit`}
        {...getAccessibilityProps({ role: 'button', label: isBusy ? busyLabel : submitLabel })}>
        {isBusy ? <ButtonSpinner /> : null}
        <ButtonText className="text-secondary-700">{isBusy ? busyLabel : submitLabel}</ButtonText>
      </Button>
    </VStack>
  );
}
